import { motion, AnimatePresence } from "framer-motion";
import { X, MagnifyingGlass, Star } from "@phosphor-icons/react";

/**
 * FiltersDrawer — bottom sheet hosting the secondary filter controls
 * that used to clutter the floating top header: search input,
 * near-me distance toggle, hide-past toggle, the food-category chip
 * rail, and the "⭐ Your Trucks" follow-only toggle.
 *
 * Accepts the live filter state from MapView so changes apply
 * immediately (no apply button — the sheet acts like a control
 * panel, dismissed when the user taps Done or the backdrop).
 *
 * Props (all required):
 *   open / onClose
 *   searchQuery / onSearchQueryChange
 *   nearMeButton: rendered as-is (caller owns disabled-state logic)
 *   hidePast / onHidePastChange
 *   categories: array of {slug, label, emoji} viable for the active filter
 *   activeCategory / onCategoryChange
 *   followedActive / followedCount / onToggleFollowed: drives the
 *     "Your Trucks" toggle (hidden when followedCount === 0)
 *   activeCount: number of filters currently engaged (for the title)
 */
export default function FiltersDrawer({
  open,
  onClose,
  searchQuery,
  onSearchQueryChange,
  nearMeButton,
  hidePast,
  onHidePastChange,
  categories,
  activeCategory,
  onCategoryChange,
  followedActive,
  followedCount = 0,
  onToggleFollowed,
  activeCount = 0,
  onClearAll,
}) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            // pointer-events-auto + z-[1000] — see DateFilterPicker for
            // the full explanation. Inheriting `pointer-events: none`
            // from the floating-header parent + Leaflet's z-800 controls
            // were both intercepting clicks on production.
            className="pointer-events-auto fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm"
            data-testid="filters-drawer-backdrop"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Filters"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 320 }}
            className="pointer-events-auto fixed inset-x-0 bottom-0 z-[1001] bg-card border-t border-border rounded-t-3xl shadow-2xl max-h-[88dvh] overflow-y-auto"
            data-testid="filters-drawer"
          >
            <div className="flex justify-center pt-2.5 pb-1">
              <div className="h-1.5 w-10 rounded-full bg-muted" />
            </div>
            <div className="px-5 pb-6 pt-2 max-w-xl mx-auto space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-xl tracking-tight">
                  Filters
                  {activeCount > 0 && (
                    <span className="ml-2 text-xs font-semibold bg-primary text-primary-foreground rounded-full px-2 py-0.5 align-middle">
                      {activeCount}
                    </span>
                  )}
                </h2>
                <button
                  type="button"
                  onClick={onClose}
                  className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-muted-foreground"
                  aria-label="Close"
                  data-testid="filters-drawer-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Search */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Search
                </label>
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => onSearchQueryChange(e.target.value)}
                    placeholder="Search truck name"
                    className="h-11 w-full rounded-full bg-background border border-border pl-10 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    data-testid="filters-drawer-search"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => onSearchQueryChange("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-full text-muted-foreground hover:bg-muted"
                      aria-label="Clear search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Near-me + hide-past */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Distance & timing
                </label>
                <div className="flex flex-wrap gap-2">
                  {nearMeButton}
                  <button
                    type="button"
                    onClick={() => onHidePastChange(!hidePast)}
                    aria-pressed={hidePast}
                    className={
                      "inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-semibold border transition-colors " +
                      (hidePast
                        ? "bg-primary text-primary-foreground border-transparent"
                        : "bg-background border-border hover:bg-muted")
                    }
                    data-testid="filters-drawer-hide-past"
                  >
                    <span
                      className={
                        "h-2 w-2 rounded-full " +
                        (hidePast ? "bg-primary-foreground/80" : "bg-muted-foreground/60")
                      }
                    />
                    {hidePast ? "Hiding past events" : "Show past events"}
                  </button>
                </div>
              </div>

              {/* Followed-only toggle — only shown when the visitor has
                  followed at least one truck. Switches the page from the
                  current date filter to the "followed" view. */}
              {followedCount > 0 && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Show
                  </label>
                  <button
                    type="button"
                    onClick={onToggleFollowed}
                    aria-pressed={!!followedActive}
                    className={
                      "inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold border transition-colors " +
                      (followedActive
                        ? "bg-primary text-primary-foreground border-transparent"
                        : "bg-background border-border hover:bg-muted")
                    }
                    data-testid="filters-drawer-followed"
                  >
                    <Star weight={followedActive ? "fill" : "duotone"} className="h-4 w-4" />
                    Only my followed trucks
                    <span
                      className={
                        "inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 text-[11px] font-bold rounded-full " +
                        (followedActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-primary/10 text-primary")
                      }
                    >
                      {followedCount}
                    </span>
                  </button>
                </div>
              )}

              {/* Categories */}
              {categories && categories.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Food category
                  </label>
                  <div className="flex flex-wrap gap-2" data-testid="filters-drawer-categories">
                    <button
                      type="button"
                      onClick={() => onCategoryChange("")}
                      className={
                        "h-9 px-3.5 rounded-full text-xs font-semibold border transition-colors " +
                        (activeCategory === ""
                          ? "bg-primary text-primary-foreground border-transparent"
                          : "bg-background border-border hover:bg-muted")
                      }
                      data-testid="filters-drawer-cat-all"
                    >
                      All
                    </button>
                    {categories.map((c) => {
                      const active = activeCategory === c.slug;
                      return (
                        <button
                          key={c.slug}
                          type="button"
                          onClick={() => onCategoryChange(active ? "" : c.slug)}
                          className={
                            "h-9 px-3.5 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1 " +
                            (active
                              ? "bg-primary text-primary-foreground border-transparent"
                              : "bg-background border-border hover:bg-muted")
                          }
                          data-testid={`filters-drawer-cat-${c.slug}`}
                        >
                          <span>{c.emoji}</span>
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-2 sticky bottom-0 pb-1 bg-card pt-3">
                {activeCount > 0 && (
                  <button
                    type="button"
                    onClick={onClearAll}
                    className="flex-1 h-12 rounded-full border border-border bg-background hover:bg-muted text-sm font-semibold transition"
                    data-testid="filters-drawer-clear"
                  >
                    Clear all
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 h-12 rounded-full bg-primary text-primary-foreground text-sm font-bold shadow-sm hover:bg-primary/90 transition"
                  data-testid="filters-drawer-done"
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
